import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { saveDragonScore, getMyDragonHighScore, getDragonLeaderboard } from '../../services/gameApi';
import BackButton from '../../components/BackButton';
import LoginPrompt from '../../components/LoginPrompt';
import GameCanvas from './dragDragon/GameCanvas';
import { DIFFICULTY } from './dragDragon/DifficultyConfig';
import './DragDragon.css';

export default function DragDragon() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState('MEDIUM');
  const [score, setScore] = useState(0);
  const [diamonds, setDiamonds] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DIFFICULTY.MEDIUM.gameTime);
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [collisions, setCollisions] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const timerRef = useRef(null);

  const config = DIFFICULTY[difficulty];

  // Load high score from backend (only if authenticated)
  useEffect(() => {
    const loadHighScore = async () => {
      if (!isAuthenticated) {
        // For anonymous users, load from localStorage
        const savedHighScore = localStorage.getItem('dragonHighScore');
        if (savedHighScore) {
          setHighScore(parseInt(savedHighScore, 10));
        }
        return;
      }
      
      try {
        const response = await getMyDragonHighScore();
        if (response.data && response.data.highScore) {
          setHighScore(response.data.highScore);
        }
      } catch (error) {
        console.error('Failed to load high score:', error);
        const savedHighScore = localStorage.getItem('dragonHighScore');
        if (savedHighScore) {
          setHighScore(parseInt(savedHighScore, 10));
        }
      }
    };
    loadHighScore();
  }, [isAuthenticated]);

  // Game timer
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (timeLeft === 0) {
      setGameOver(true);
      saveScoreToBackend();
      if (score > highScore) {
        setHighScore(score);
        localStorage.setItem('dragonHighScore', score.toString());
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeLeft, gameStarted, gameOver, score, highScore]);

  const startGame = (selectedDifficulty = difficulty) => {
    const config = DIFFICULTY[selectedDifficulty];
    setDifficulty(selectedDifficulty);
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setDiamonds(0);
    setTimeLeft(config.gameTime);
    setCollisions(0);
  };

  const saveScoreToBackend = async () => {
    try {
      if (isAuthenticated) {
        // Save to backend if authenticated
        await saveDragonScore(score, diamonds, collisions);
      } else {
        // For anonymous users, save to localStorage for later sync
        const pendingScore = {
          score,
          diamonds,
          collisions,
          timestamp: Date.now()
        };
        localStorage.setItem('pendingDragonScore', JSON.stringify(pendingScore));
        console.log('Score saved locally. Login to sync with server.');
      }
    } catch (error) {
      console.error('Failed to save score:', error);
      // Fallback to localStorage if backend save fails
      if (isAuthenticated) {
        const pendingScore = {
          score,
          diamonds,
          collisions,
          timestamp: Date.now()
        };
        localStorage.setItem('pendingDragonScore', JSON.stringify(pendingScore));
      }
    }
  };

  const handleCollision = () => {
    setCollisions((prev) => prev + 1);
    setScore((prev) => Math.max(0, prev - 10)); // Penalty
  };

  const handleDiamondCollected = () => {
    setDiamonds((prev) => prev + 1);
  };

  const handleScore = (points) => {
    setScore((prev) => prev + points);
  };

  const loadLeaderboard = async () => {
    if (!isAuthenticated) {
      // Show login prompt instead of loading leaderboard
      setShowLoginPrompt(true);
      return;
    }
    
    setLoading(true);
    try {
      const response = await getDragonLeaderboard();
      setLeaderboard(response.data || []);
      setShowLeaderboard(true);
    } catch (error) {
      if (error.response?.status === 401) {
        setShowLoginPrompt(true);
      } else {
        console.error('Failed to load leaderboard:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetGame = () => {
    const config = DIFFICULTY[difficulty];
    setGameStarted(false);
    setGameOver(false);
    setScore(0);
    setDiamonds(0);
    setTimeLeft(config.gameTime);
    setCollisions(0);
    setShowLeaderboard(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  return (
    <div className="drag-dragon-container">
      <BackButton />
      <div className="game-header">
        <h1>🐉 Drag the Dragon</h1>
        <div className="game-stats">
          <div className="stat">
            <span className="stat-label">Score:</span>
            <span className="stat-value">{score}</span>
          </div>
          <div className="stat">
            <span className="stat-label">💎 Diamonds:</span>
            <span className="stat-value">{diamonds}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Time:</span>
            <span className="stat-value">{timeLeft}s</span>
          </div>
          <div className="stat">
            <span className="stat-label">Collisions:</span>
            <span className="stat-value">{collisions}</span>
          </div>
          <div className="stat">
            <span className="stat-label">High Score:</span>
            <span className="stat-value">{highScore}</span>
          </div>
          {gameStarted && (
            <div className="stat">
              <span className="stat-label">Difficulty:</span>
              <span className="stat-value">{config.name}</span>
            </div>
          )}
        </div>
      </div>

      {!gameStarted && !gameOver && !showLeaderboard && (
        <div className="game-start-screen">
          <h2>Guide the Dragon Through Obstacles!</h2>
          <p>Drag your mouse or finger to move the dragon. Collect diamonds and avoid obstacles!</p>
          
          <div className="difficulty-selection">
            <h3>Select Difficulty:</h3>
            <div className="difficulty-buttons">
              <button
                className={`difficulty-btn ${difficulty === 'EASY' ? 'active' : ''}`}
                onClick={() => setDifficulty('EASY')}
              >
                <span className="difficulty-icon">😊</span>
                <span className="difficulty-name">Easy</span>
                <span className="difficulty-desc">60s • Slow • Relaxed</span>
              </button>
              <button
                className={`difficulty-btn ${difficulty === 'MEDIUM' ? 'active' : ''}`}
                onClick={() => setDifficulty('MEDIUM')}
              >
                <span className="difficulty-icon">😐</span>
                <span className="difficulty-name">Medium</span>
                <span className="difficulty-desc">45s • Normal • Balanced</span>
              </button>
              <button
                className={`difficulty-btn ${difficulty === 'HARD' ? 'active' : ''}`}
                onClick={() => setDifficulty('HARD')}
              >
                <span className="difficulty-icon">😤</span>
                <span className="difficulty-name">Hard</span>
                <span className="difficulty-desc">30s • Fast • Intense</span>
              </button>
            </div>
          </div>

          <div className="game-buttons">
            <button className="start-button" onClick={() => startGame(difficulty)}>
              Start Game ({config.name})
            </button>
            <button 
              className={`leaderboard-button ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={loadLeaderboard}
              disabled={!isAuthenticated}
              title={!isAuthenticated ? 'Login to view leaderboard' : 'View leaderboard'}
            >
              {isAuthenticated ? 'View Leaderboard' : 'Login to View Leaderboard'}
            </button>
          </div>
        </div>
      )}

      {showLoginPrompt && (
        <LoginPrompt
          feature="games"
          subFeature="leaderboard"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      {showLeaderboard && (
        <div className="leaderboard-screen">
          <h2>🏆 Leaderboard</h2>
          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              <div className="leaderboard-list">
                {leaderboard.length === 0 ? (
                  <p>No scores yet. Be the first!</p>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="username">{entry.username}</span>
                      <span className="score">{entry.score}</span>
                      <span className="diamonds">💎 {entry.diamonds}</span>
                    </div>
                  ))
                )}
              </div>
              <button className="reset-button" onClick={() => setShowLeaderboard(false)}>
                Back
              </button>
            </>
          )}
        </div>
      )}

      {gameOver && (
        <div className="game-over-screen">
          <h2>Game Over!</h2>
          <div className="final-stats">
            <p>Final Score: <strong>{score}</strong></p>
            <p>Diamonds Collected: <strong>{diamonds}</strong></p>
            <p>Collisions: <strong>{collisions}</strong></p>
            {score === highScore && <p className="new-high-score">🎉 New High Score!</p>}
          </div>
          <div className="game-buttons">
            <button className="start-button" onClick={() => startGame(difficulty)}>
              Play Again ({config.name})
            </button>
            <button 
              className={`leaderboard-button ${!isAuthenticated ? 'disabled' : ''}`}
              onClick={loadLeaderboard}
              disabled={!isAuthenticated}
              title={!isAuthenticated ? 'Login to view leaderboard' : 'View leaderboard'}
            >
              {isAuthenticated ? 'View Leaderboard' : 'Login to View Leaderboard'}
            </button>
            <button className="reset-button" onClick={resetGame}>
              Main Menu
            </button>
          </div>
        </div>
      )}

      {gameStarted && !gameOver && (
        <div className="game-board-container">
          <GameCanvas
            difficulty={difficulty}
            config={config}
            onScore={handleScore}
            onDiamondCollected={handleDiamondCollected}
            onGameOver={() => {
              setGameOver(true);
              saveScoreToBackend();
              if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('dragonHighScore', score.toString());
              }
            }}
            onCollision={handleCollision}
          />
        </div>
      )}
    </div>
  );
}
